import { get, patch, post, put, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";
import instance from "../http-client/client";

// ============================================================================
// INTERFACES
// ============================================================================

export type ProjectFileStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface Project {
  id: string;
  name: string;
  description?: string;
  isShared: boolean;
  /** private | organization | members */
  visibility?: ProjectVisibility;
  /** standard, or compare for the private "My comparisons" project */
  kind?: "standard" | "compare";
  /** Your access: owner, editor or viewer */
  access?: "owner" | "editor" | "viewer";
  /** Owner's name, for projects shared with you */
  ownerName?: string;
  userId: string;
  organizationId: string;
  fileSearchStoreId?: string;
  fileCount: number;
  playbookCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  status: ProjectFileStatus;
  projectId: string;
  uploadedById: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectPlaybook {
  id: string;
  playbookId: string;
  playbookName: string;
  playbookAgreementType: string;
  createdAt: Date;
}

// ============================================================================
// REQUEST DTOS
// ============================================================================

export interface CreateProjectDto {
  name: string;
  description?: string;
  isShared?: boolean;
}

export interface UpdateProjectDto {
  name?: string;
  description?: string;
  isShared?: boolean;
}

export interface LinkPlaybookDto {
  playbookId: string;
}

// ============================================================================
// API SERVICE FUNCTIONS - Projects
// ============================================================================

/**
 * Create a new project
 */
export const createProject = (
  data: CreateProjectDto
): Promise<ApiResponse<Project>> => {
  return post<Project>("/projects", data, null);
};

/**
 * Get all projects accessible to the user
 */
export const getProjects = (): Promise<ApiResponse<Project[]>> => {
  return get<Project[]>("/projects", null);
};

/**
 * Get a specific project by ID
 */
export const getProject = (projectId: string): Promise<ApiResponse<Project>> => {
  return get<Project>(`/projects/${projectId}`, null);
};

/**
 * Update an existing project
 */
export const updateProject = (
  projectId: string,
  data: UpdateProjectDto
): Promise<ApiResponse<Project>> => {
  return patch<Project>(`/projects/${projectId}`, data, null);
};

/**
 * Delete a project
 */
export const deleteProject = (projectId: string): Promise<ApiResponse<void>> => {
  return remove<void>(`/projects/${projectId}`, null);
};

// ============================================================================
// API SERVICE FUNCTIONS - Files
// ============================================================================

/**
 * Get files in a project
 */
export const getProjectFiles = (
  projectId: string
): Promise<ApiResponse<ProjectFile[]>> => {
  return get<ProjectFile[]>(`/projects/${projectId}/files`, null);
};

/**
 * Upload a file to a project
 */
export const uploadProjectFile = async (
  projectId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ApiResponse<ProjectFile>> => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await instance.post<{ data: ProjectFile }>(
      `/projects/${projectId}/files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error: any) {
    return {
      success: false,
      status: error.response?.status,
      errors: error.response?.data?.errors || [{ title: "Failed to upload file" }],
    };
  }
};

/**
 * Delete a file from a project
 */
export const deleteProjectFile = (
  projectId: string,
  fileId: string
): Promise<ApiResponse<void>> => {
  return remove<void>(`/projects/${projectId}/files/${fileId}`, null);
};

/**
 * Get a download URL for a file
 */
export const getFileDownloadUrl = (
  projectId: string,
  fileId: string
): Promise<ApiResponse<{ url: string; filename: string }>> => {
  return get<{ url: string; filename: string }>(
    `/projects/${projectId}/files/${fileId}/download`,
    null
  );
};

// ============================================================================
// API SERVICE FUNCTIONS - Playbooks
// ============================================================================

/**
 * Get playbooks linked to a project
 */
export const getProjectPlaybooks = (
  projectId: string
): Promise<ApiResponse<ProjectPlaybook[]>> => {
  return get<ProjectPlaybook[]>(`/projects/${projectId}/playbooks`, null);
};

/**
 * Link a playbook to a project
 */
export const linkPlaybook = (
  projectId: string,
  data: LinkPlaybookDto
): Promise<ApiResponse<ProjectPlaybook>> => {
  return post<ProjectPlaybook>(`/projects/${projectId}/playbooks`, data, null);
};

/**
 * Link all of the user's playbooks to a project at once (idempotent).
 * Used by the drag-and-drop flow so a dropped document references every
 * available playbook automatically. Returns the full linked list.
 */
export const linkAllPlaybooks = (
  projectId: string,
): Promise<ApiResponse<ProjectPlaybook[]>> => {
  return post<ProjectPlaybook[]>(`/projects/${projectId}/playbooks/link-all`, {}, null);
};

/**
 * Unlink a playbook from a project
 */
export const unlinkPlaybook = (
  projectId: string,
  playbookId: string
): Promise<ApiResponse<void>> => {
  return remove<void>(`/projects/${projectId}/playbooks/${playbookId}`, null);
};

/**
 * Add a copy of one of the organization's standard agreements to a project
 * (reuses an identical copy already there)
 */
export const addStandardAgreementToProject = (
  projectId: string,
  standardAgreementId: string,
): Promise<ApiResponse<ProjectFile>> =>
  post<ProjectFile>(
    `/projects/${projectId}/files/from-standard-agreement/${standardAgreementId}`,
    {},
    null,
  );

export type ProjectVisibility = "private" | "organization" | "members";
export type ProjectRole = "editor" | "viewer";

export interface ProjectSharing {
  visibility: ProjectVisibility;
  organizationRole: ProjectRole;
  members: { userId: string; role: ProjectRole }[];
}

export const getProjectSharing = (projectId: string): Promise<ApiResponse<ProjectSharing>> =>
  get(`/projects/${projectId}/sharing`, null);

export const updateProjectSharing = (
  projectId: string,
  sharing: ProjectSharing,
): Promise<ApiResponse<ProjectSharing>> => put(`/projects/${projectId}/sharing`, sharing);
